<template>
  <div>
    <vue-data-table
      :headers="headers"
      :data="transactions"
      :items-per-page="10"
      :buttons="buttons"
      :summable-columns="summableColumns"
      :filter-fields="filterFields"
      :filter-data="filter"
      filter-title="Filtrer les transactions"
      :tableActions="tableActions"
      @filter="toFilter"
      @reset="resetFilter"
    >
      <!-- Employee Column -->
      <template #cell-employee="{ row }">
        <span v-if="row.employee" :class="row.employee.deleted_at || row.employee.blocked || row.employee.fullName.includes('Bloqué')
          ? 'text-danger text-decoration-line-through'
          : ''
          ">
          {{ row.employee.fullName }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Amount Column -->
      <template #cell-amount="{ row }">
        <span class="fw-bold text-primary">
          {{ row.amount | toFixedWithSpace }} DH
        </span>
      </template>

      <!-- Transaction Method Column -->
      <template #cell-transactionMethod="{ row }">
        <span class="badge" :class="getMethodBadgeClass(row.transactionMethod)">
          {{ row.transactionMethod || '—' }}
        </span>
      </template>

      <!-- Destination Column -->
      <template #cell-destination="{ row }">
        <span v-if="row.destination" class="text-muted">
          {{ row.destination }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Check Number Column -->
      <template #cell-checkNumber="{ row }">
        <span v-if="row.checkNumber" class="text-info">
          {{ row.checkNumber }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Remark Column -->
      <template #cell-remark="{ row }">
        <span v-if="row.remark" class="text-muted" :title="row.remark">
          {{ row.remark.length > 30 ? row.remark.substring(0, 30) + '...' : row.remark }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>

      <!-- Date Column -->
      <template #cell-date="{ row }">
        <span v-if="row.date">
          {{ formatDate(row.date) }}
        </span>
        <span v-else class="text-muted">—</span>
      </template>
    </vue-data-table>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

const defaultFilter = () => ({
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date().toISOString().split('T')[0],
  employee_reference: "",
  transactionMethod: "",
});

export default {
  data() {
    return {
      filter: defaultFilter(),
      tableActions: [
        {
          title: "Actualiser",
          icon: "bi bi-arrow-clockwise",
          class: "btn btn-secondary",
          action: () => this.refresh(),
        },
        {
          title: "Créer une nouvelle transaction",
          icon: "bi bi-plus-square",
          class: "btn btn-primary",
          action: () => this.$router.push({ name: "transactions-create" }),
        },
      ],
      headers: [
        { field: "reference", label: "Référence", class: "text-left fw-bold" },
        { field: "employee", label: "Employee", class: "text-left" },
        { field: "date", label: "Date", class: "text-center" },
        { field: "amount", label: "Montant", class: "text-end fw-bold" },
        { field: "transactionMethod", label: "Mode de paiement", class: "text-center" },
        { field: "destination", label: "Destination", class: "text-left" },
        { field: "checkNumber", label: "N° Chèque", class: "text-center" },
        { field: "remark", label: "Remarque", class: "text-left" },
      ],
      buttons: [
        {
          label: "Détails",
          icon: "bi bi-eye",
          class: "btn btn-info",
          action: (row) => {
            this.$router.push({
              name: "transactions-details",
              params: { reference: row.reference },
            });
          },
        },
        {
          label: "Modifier",
          icon: "bi bi-pencil",
          class: "btn btn-warning",
          action: (row) => {
            this.$router.push({
              name: "transactions-edit",
              params: { reference: row.reference },
            });
          },
          condition: () => this.$userRole === "Admin"
        },
        {
          label: "Supprimer",
          icon: "bi bi-trash",
          class: "btn btn-danger",
          action: (row) => this.remove(row.reference),
          condition: () => this.$userRole === "Admin"
        }
      ],
      summableColumns: [
        { field: 'amount', class: 'text-primary fw-bold' },
      ]
    };
  },
  computed: {
    ...mapGetters("transaction", {
      transactions: "getTransactions",
      totalAmount: "getTotalAmount"
    }),
    ...mapGetters("employee", {
      employees: "getAll"
    }),
    filterFields() {
      return [
        {
          field: "employee_reference",
          label: "Employee",
          type: "select",
          options: this.employees.map(emp => ({
            value: emp.reference,
            label: emp.fullName
          })),
          required: false
        },
        {
          field: "transactionMethod",
          label: "Mode de paiement",
          type: "select",
          options: [
            { value: "Espèce", label: "Espèce" },
            { value: "Virement bancaire", label: "Virement bancaire" },
            { value: "Chèque", label: "Chèque" },
            { value: "Effet", label: "Effet" }
          ],
          required: false
        },
        {
          field: "startDate",
          label: "Du",
          type: "date",
          required: true
        },
        {
          field: "endDate",
          label: "Au",
          type: "date",
          required: true
        }
      ];
    }
  },
  mounted() {
    this.$store.dispatch("transaction/getAll");
    this.$store.dispatch("employee/getAll");
  },
  methods: {
    async refresh() {
      await this.$store.dispatch("transaction/getAll");
    },
    async toFilter(data) {
      await this.$store.dispatch("transaction/filter", data);
    },
    resetFilter() {
      this.filter = defaultFilter();
      this.toFilter(this.filter);
    },
    async remove(reference) {
      await this.$confirm({
        message: `Voulez-vous vraiment supprimer la transaction ${reference}?`,
        button: {
          no: "Non",
          yes: "Oui",
        },
        callback: async (confirm) => {
          if (confirm) {
            await this.$store.dispatch("transaction/destroy", reference);
            await this.refresh();
          }
        },
      });
    },
    getMethodBadgeClass(method) {
      const classes = {
        'Espèce': 'bg-success',
        'Espèces': 'bg-success',
        'Chèque': 'bg-primary',
        'Virement bancaire': 'bg-info',
        'Virement': 'bg-info',
        'Effet': 'bg-warning',
        'Carte': 'bg-warning'
      };
      return classes[method] || 'bg-secondary';
    },
    formatDate(date) {
      if (!date) return '—';
      const d = new Date(date);
      return d.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    }
  },
  filters: {
    toFixedWithSpace: (value) => {
      return parseFloat(value)
        .toFixed(2)
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
    }
  }
};
</script>

<style scoped>
.text-decoration-line-through {
  text-decoration: line-through;
}
</style>